import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tmApi, type TmProjectView, type TmUserOption } from "../../api/tm";
import { useToast } from "../../components/Toast";
import { UserCombobox } from "../../components/tm/UserCombobox";
import { ProjectCombobox } from "../../components/tm/ProjectCombobox";
import { TmRecurrenceForm } from "../../components/tm/TmRecurrenceForm";
import type { ApiError } from "../../api/client";

export default function TmMeetingNewPage() {
  const { canWrite } = useSectionAccess("tm_meetings");
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [projects, setProjects] = useState<TmProjectView[]>([]);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState("");
  const [agenda, setAgenda] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [scribeUserId, setScribeUserId] = useState<string | null>(null);
  const [recurrenceRule, setRecurrenceRule] = useState<string | null>(null);

  useEffect(() => {
    void tmApi.users().then(setUsers).catch(() => undefined);
    void tmApi.listProjects().then(setProjects).catch(() => undefined);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setBusy(true);
    try {
      const created = await tmApi.createMeeting({
        title,
        agenda: agenda || null,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes: duration,
        location: location || null,
        projectId: projectId || null,
        scribeUserId: scribeUserId || null,
        recurrenceRule: recurrenceRule || null,
      });
      toast.success(t("tm.meetings.toastCreated", "Meeting created."));
      navigate(`/tm/meetings/${created.id}`, { replace: true });
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
          {t("tm.meetings.new.title", "New meeting")}
        </h1>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <label className="field">
            <span>{t("tm.meetings.new.titleLabel", "Title")} *</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
          </label>
          <label className="field">
            <span>{t("tm.meetings.new.agenda", "Agenda")}</span>
            <textarea className="input" value={agenda} onChange={(e) => setAgenda(e.target.value)} rows={4} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("tm.meetings.new.when", "When")} *</span>
              <input type="datetime-local" className="input" value={scheduledAt}
                     onChange={(e) => setScheduledAt(e.target.value)} required />
            </label>
            <label className="field">
              <span>{t("tm.meetings.new.duration", "Duration (min)")}</span>
              <input type="number" className="input" min={5} max={480} value={duration}
                     onChange={(e) => setDuration(Number(e.target.value))} />
            </label>
            <label className="field">
              <span>{t("tm.meetings.new.location", "Location")}</span>
              <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} />
            </label>
            <label className="field">
              <span>{t("tm.meetings.new.project", "Project (optional)")}</span>
              <ProjectCombobox projects={projects} value={projectId} onChange={setProjectId} />
            </label>
            <label className="field" style={{ gridColumn: "span 2" }}>
              <span>{t("tm.meetings.new.scribe", "Scribe (optional)")}</span>
              <UserCombobox users={users} value={scribeUserId} onChange={setScribeUserId} />
            </label>
          </div>

          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--ink-3)",
                          letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 8 }}>
              {t("tm.meetings.new.recurrence", "Recurrence")}
            </div>
            <TmRecurrenceForm value={recurrenceRule} onChange={setRecurrenceRule} />
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: "14px 18px", borderTop: "1px solid var(--border)",
          background: "var(--surface-2)", borderRadius: "0 0 var(--r-md) var(--r-md)",
        }}>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy || !canWrite}>
            {busy ? t("tm.meetings.new.creating", "Creating…") : t("tm.meetings.new.create", "Create meeting")}
          </button>
        </div>
      </div>
    </form>
  );
}
